import { useQuery } from '@tanstack/react-query';
import { legalAgreementsAdminApi } from '@/lib/pillarApi';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type LegalConfigurationHealthWarning,
} from '@pillar-portal/api-types';

/** One plain sentence per configuration problem. */
export function describeLegalConfigurationHealthWarning(
    warning: LegalConfigurationHealthWarning
): string {
    switch (warning.code) {
        case 'no_platform_default':
            return 'No platform-default user agreement is in force, so users of Organizations without their own agreement are asked to accept nothing.';
        case 'platform_default_ending':
            return `The platform default "${warning.legal_document_title}" stops applying on ${formatTimestampForMerchantUi(warning.ends_at, SYSTEM_DISPLAY_TIME_ZONE)} and nothing replaces it.`;
        case 'assigned_document_unavailable':
            return `${warning.legal_document.legal_document_title} (${warning.legal_document.legal_document_version}) is assigned to ${warning.organization_name ?? 'all Organizations as the platform default'} but does not apply, because the document is not active or not in force.`;
    }
}

/** Persistent warnings at the top of the legal administration area. */
export function LegalConfigurationHealthBanner() {
    const healthQuery = useQuery({
        queryKey: ['legalConfigurationHealth'],
        queryFn: async () => await legalAgreementsAdminApi.getConfigurationHealth(),
        staleTime: 30_000,
    });
    if (healthQuery.isError) {
        return (
            <div
                role="alert"
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
                Unable to check the legal configuration.
            </div>
        );
    }
    const warnings = healthQuery.data?.warnings ?? [];
    if (warnings.length === 0) {
        return null;
    }
    return (
        <div
            role="alert"
            aria-label="Legal configuration warnings"
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
            <ul className="list-disc space-y-1 pl-5">
                {warnings.map((warning, index) => (
                    <li key={`${warning.code}-${index}`}>
                        {describeLegalConfigurationHealthWarning(warning)}
                    </li>
                ))}
            </ul>
        </div>
    );
}
